<template>
  <div
    v-if="paymentMethod && paymentMethodConfig"
    class="flex justify-center md:flex-auto"
  >
    <div class="w-full max-w-[50rem]">
      <h1 class="heading-1 mb-12">
        {{ i18n(translations.title) }}
      </h1>

      <RevCard class="w-full p-12 md:p-32">
        <div class="flex justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.subtitle) }}
          </h2>

          <PaymentIcons
            v-if="paymentMethodCode === PaymentMethodCode.card"
            :methods="tradeInPaymentMethods"
          ></PaymentIcons>
        </div>

        <SinglePaymentMethodForm
          ref="paymentForm"
          :base-price="{ amount: '0', currency: Currency.EUR }"
          :create-payment
          :payment-method
          @setup-error="handleSetupError"
          @submit-error="handleSubmitError"
          @submit-success="handleSubmitSuccess"
        />
        <div class="my-8 flex justify-end">
          <PaymentSubmitButton :form="paymentForm">
            {{ i18n(translations.submitButtonLabel) }}
          </PaymentSubmitButton>
        </div>
      </RevCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createError, navigateTo, useRequestURL, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { Currency } from '@backmarket/http-api'
import {
  getRefurbisherPaymentMethodsConfig,
  postRefurbisherPaymentTokenization,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import {
  PaymentMethodCode,
  type PostRefurbisherPaymentTokenization,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import type { CreatePaymentResponse } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import {
  type PaymentFormInstance,
  PaymentSubmitButton,
  SinglePaymentMethodForm,
  type SubmitErrorEvent,
} from '@backmarket/nuxt-layer-payment/form'
import {
  PaymentIcons,
  useMarketPaymentMethodsFor,
} from '@backmarket/nuxt-layer-payment/methods'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'

import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import allTranslations from './UpdatePaymentMethod.translations'
import type { Translations } from './UpdatePaymentMethod.types'

const i18n = useI18n()
const { companyName } = useSellerInfoStore()
const logger = useLogger()
const currentUrl = useRequestURL()
const route = useRoute()
const { openErrorToast, openSuccessToast } = useTheToast()

const paymentForm = ref<PaymentFormInstance | null>(null)

const [
  { data: tradeInPaymentMethods },
  { error: paymentMethodsConfigError, data: paymentMethodsConfig },
] = await Promise.all([
  useMarketPaymentMethodsFor({
    tenant: 'TRADEIN',
  }),
  useHttpFetch(getRefurbisherPaymentMethodsConfig),
])

const paymentMethodCode = computed((): PaymentMethodCode => {
  const codeFromRoute = route.params.code as PaymentMethodCode

  const codeExists = tradeInPaymentMethods.value?.find(
    (method) => method.bmCode === route.params.code,
  )

  if (!codeExists) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
  }

  return codeFromRoute
})

const paymentMethod = computed(() => {
  return tradeInPaymentMethods.value?.find(
    (method) => method.bmCode === paymentMethodCode.value,
  )
})

if (tradeInPaymentMethods.value) {
  logger.info(
    `[BUYBACK][API][PAYMENT_METHOD] TradeIn payment methods for seller ${companyName}`,
    {
      config: tradeInPaymentMethods.value,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    },
  )
}

if (paymentMethodsConfig.value) {
  logger.info(
    `[BUYBACK][API][PAYMENT_METHOD] Payment methods config for seller ${companyName}`,
    {
      config: paymentMethodsConfig.value,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    },
  )
}

if (paymentMethodsConfigError.value) {
  logger.error('[BUYBACK][API][PAYMENT_METHOD] Error getting payment methods', {
    error: paymentMethodsConfigError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

const paymentMethodConfig = computed(() => {
  if (!paymentMethodsConfig.value) {
    return null
  }

  const { currenciesPaymentMethods } = paymentMethodsConfig.value

  return currenciesPaymentMethods[0].paymentMethods?.find(
    (method) => method.code.toLowerCase() === paymentMethodCode.value,
  )
})

const translations = computed((): Translations => {
  return {
    title: allTranslations[`${paymentMethodCode.value}_title`],
    subtitle: allTranslations[`${paymentMethodCode.value}_subtitle`],
    toastSuccessTitle:
      allTranslations[`${paymentMethodCode.value}_toastSuccessTitle`],
    toastSuccessContent:
      allTranslations[`${paymentMethodCode.value}_toastSuccessContent`],
    toastErrorTitle:
      allTranslations[`${paymentMethodCode.value}_toastErrorTitle`],
    toastErrorContent:
      allTranslations[`${paymentMethodCode.value}_toastErrorContent`],
    submitButtonLabel:
      allTranslations[`${paymentMethodCode.value}_submitButtonLabel`],
  }
})

async function createPayment(): Promise<CreatePaymentResponse> {
  if (!paymentMethodCode.value || !paymentMethodConfig.value) {
    throw new Error('createPayment method is not initialized')
  }

  const body: PostRefurbisherPaymentTokenization = {
    successUrl: currentUrl.toString(),
    failureUrl: currentUrl.toString(),
    cancelUrl: currentUrl.toString(),
    paymentMethod: {
      code: paymentMethodCode.value.toUpperCase(),
      reference: paymentMethodConfig.value?.reference,
      gateway: paymentMethodConfig.value?.gateway,
    },
  }

  const tokenizationResponse = await $httpFetch(
    postRefurbisherPaymentTokenization,
    { body },
  )

  const createPaymentResponse: CreatePaymentResponse = {
    paymentId: tokenizationResponse.reference,
    gatewayReference: tokenizationResponse.gatewayReference,
    gatewayTokenReference: tokenizationResponse.gatewayTokenReference,
  }

  return createPaymentResponse
}

function handleSetupError() {
  openErrorToast()
}

function handleSubmitSuccess() {
  openSuccessToast({
    title: i18n(translations.value.toastSuccessTitle),
    content: i18n(translations.value.toastSuccessContent),
  })

  navigateTo({
    name: ROUTE_NAMES.BUYBACK.OPTIONS.PAYMENT,
  })
}

function handleSubmitError(event: SubmitErrorEvent) {
  openErrorToast({
    title: i18n(translations.value.toastErrorTitle),
    content: i18n(translations.value.toastErrorContent),
  })

  logger.error(
    `[BUYBACK][API][PAYMENT_METHOD] Error updating ${paymentMethodCode.value} banking info`,
    {
      error: event.cause,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    },
  )
}
</script>
